import { useState } from "react";
import { useTranslation } from "react-i18next";
import {
  type WorkflowStepType,
  type WorkflowStepUser,
  type WorkflowStepInstanceCommentType,
  WorkflowStepInstanceCommentTypes,
  WorkflowStepUserPermissions,
  WorkflowStepUserResponses,
} from "~/api/types";
import { Button } from "~/atoms/button";
import { Card } from "~/atoms/card";
import { ConfirmDialog } from "./ConfirmDialog";
import { Label } from "~/atoms/label";
import { toast } from "react-toastify";
import { createWorkflowStepInstanceComment } from "~/api/v2/requests";
import { usePersistentState } from "~/hooks/usePersistentState";
import { RichTextEditor } from "~/molecules/RichTextEditor";

type Props = {
  currentUserInStep: WorkflowStepUser;
  stepId: number;
  instanceId: number;
  stepType: WorkflowStepType;
  previousStepName?: string;
  onUserResponse: () => void;
};

export function UserResponseForm({
  currentUserInStep,
  stepId,
  instanceId,
  stepType,
  previousStepName,
  onUserResponse,
}: Props) {
  const { t } = useTranslation();
  const [comment, setComment, clearComment] = usePersistentState<string>(
    `workflow_step_user_response:${instanceId}`,
    "",
  );
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isCommentMissingForReject, setIsCommentMissingForReject] =
    useState(false);
  const [showRejectConfirm, setShowRejectConfirm] = useState(false);

  const handleResponse = async (type: WorkflowStepInstanceCommentType) => {
    if (type === WorkflowStepInstanceCommentTypes.rejected && !comment.trim()) {
      setIsCommentMissingForReject(true);
      return;
    }

    try {
      setIsSubmitting(true);
      setIsCommentMissingForReject(false);
      await createWorkflowStepInstanceComment(stepId, type, comment || null);
      clearComment();
      onUserResponse();
    } catch (error) {
      console.error(
        t(($) => $.commons.workflow.failedToUpdateWorkflowStep),
        error,
      );
      toast.error(t(($) => $.commons.workflow.failedToUpdateWorkflowStep));
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleReject = () => {
    if (!comment.trim()) {
      setIsCommentMissingForReject(true);
      return;
    }

    if (previousStepName) {
      setShowRejectConfirm(true);
      return;
    }

    void handleResponse(WorkflowStepInstanceCommentTypes.rejected);
  };

  return (
    <>
      <Card className="bg-background ring-warning/30 p-0">
        <div className="bg-warning/10 flex flex-col gap-2 p-3">
          <div className="text-warning-foreground dark:text-warning-strong flex items-center gap-2">
            {t(($) => $.commons.workflow.reviewRespond)}
          </div>
          <div className="text-warning-foreground dark:text-warning-strong text-xs">
            {stepType === "approval"
              ? t(($) => $.commons.workflow.approvalDescription)
              : currentUserInStep.permission ===
                  WorkflowStepUserPermissions.write
                ? t(($) => $.commons.workflow.writeDescription)
                : t(($) => $.commons.workflow.readDescription)}
          </div>

          <div>
            <Label className="text-warning-foreground dark:text-warning-strong">
              {t(($) => $.commons.workflow.comment)}
            </Label>
            <RichTextEditor
              value={comment}
              onChange={(html) => {
                setComment(html);
                if (isCommentMissingForReject && html.trim()) {
                  setIsCommentMissingForReject(false);
                }
              }}
              disabled={isSubmitting}
              className="min-h-12.5"
            />
            {isCommentMissingForReject && (
              <p className="text-destructive text-sm">
                {t(($) => $.validations.workflow.rejectCommentRequired)}
              </p>
            )}
            <div className="flex flex-wrap justify-end gap-2 pt-2">
              <Button
                variant="outline"
                onClick={() =>
                  handleResponse(WorkflowStepInstanceCommentTypes.free)
                }
                disabled={isSubmitting || !comment.trim()}
                size="sm"
              >
                {t(($) => $.commons.workflow.respond.comment)}
              </Button>
              {currentUserInStep.responseStatus !==
                WorkflowStepUserResponses.rejected && (
                <Button
                  variant="destructive"
                  onClick={handleReject}
                  disabled={isSubmitting || isCommentMissingForReject}
                  size="sm"
                >
                  {t(($) => $.commons.workflow.respond.reject)}
                </Button>
              )}
              {currentUserInStep.responseStatus !==
                WorkflowStepUserResponses.approved && (
                <Button
                  onClick={() =>
                    handleResponse(WorkflowStepInstanceCommentTypes.approved)
                  }
                  disabled={isSubmitting}
                  size="sm"
                >
                  {t(($) => $.commons.workflow.respond.approve)}
                </Button>
              )}
            </div>
          </div>
        </div>
      </Card>

      <ConfirmDialog
        open={showRejectConfirm}
        onOpenChange={setShowRejectConfirm}
        title={t(($) => $.commons.workflow.rejectAndReturnTitle)}
        description={t(($) => $.commons.workflow.rejectAndReturnDescription, {
          stepName: previousStepName,
        })}
        cancelLabel={t(($) => $.commons.workflow.cancel)}
        confirmLabel={t(($) => $.commons.workflow.respond.reject)}
        confirmVariant="destructive"
        onConfirm={() =>
          void handleResponse(WorkflowStepInstanceCommentTypes.rejected)
        }
      />
    </>
  );
}
